/* eslint-disable @typescript-eslint/no-explicit-any */
import { ApolloLink, Observable, Operation } from '@apollo/client/core';
import { print } from 'graphql';
import {
    querySmartApplyState,
    mutateContactInfo,
    queryJobSeekerProfile,
    mutateResume,
    mutateQuestionsAndAnswers,
} from './localStorage-database';
import {
    comprehensiveMockPastAnswers,
    createMockPastAnswersConnection,
} from '../../pages/auto-apply-setup/past-answers/mockPastAnswers';
import { getScreenerQuestions } from '../../pages/auto-apply/questions/dummyScreenerQuestions';

/**
 * MockLink with localStorage-as-Database for Local Development
 *
 * CLEAN ARCHITECTURE: localStorage as GraphQL Database
 *
 * Flow:
 * 1. Query resolvers → Read from localStorage database → Return data
 * 2. Mutation resolvers → Update localStorage database → Return complete data
 * 3. Apollo cache updates naturally from mutation responses
 *
 * Features:
 * - Single source of truth (localStorage database)
 * - Schema-compliant data structure
 * - Natural query/mutation flow like real API
 * - Realistic network delay simulation
 * - Console debugging tools available
 *
 * Usage in browser console:
 * - window.graphqlDB.getStats() - Database statistics
 * - window.graphqlDB.listStates() - List all states
 * - window.graphqlDB.inspectState(id) - View specific state
 * - window.graphqlDB.clearDatabase() - Reset all data
 */

// =============================================================================
// LOCAL DEV TOGGLES
// =============================================================================

/**
 * Toggle the dual-resume V2 path in mocks (PROF-9839).
 *
 * When `true`, `GetAutoApplyResumes`, `GetAutoApplyReviewData`, and
 * `GetResumeType` mocks pretend the user has the dual-resume proctor active
 * and a merged profile. Pages will read uploaded file resumes from
 * `jobSeekerResumes` instead of the legacy `fileInformation`, and the file
 * resume PDF preview URL is sourced inline from `pdfResumeFile.url` (no
 * separate `DownloadFileResume*` mutation call).
 *
 * Flip locally to exercise the V2 path. Storybook stories and Vitest tests
 * provide their own MockedResponse objects and do not rely on this constant.
 */
const MOCK_DUAL_RESUMES_ENABLED = false;

// Normally values in legacy `fileInformation` and the new `jobSeekerResumes` path
// are the same due to the profio-file-syncer but we are making the values different
// here to verify that the correct data is being read.
export const MOCK_NEW_FILE_RESUME_ID = 'new_file_resume_1';
export const MOCK_NEW_FILE_RESUME_NAME = 'Taylor_J_new_resume.pdf';
export const MOCK_NEW_FILE_RESUME_UPLOAD_TIMESTAMP = '1710936000001';
export const MOCK_NEW_FILE_RESUME_PDF_URL = '/static/mock-data/new-sample-resume.pdf';
// Display date for `MOCK_NEW_FILE_RESUME_UPLOAD_TIMESTAMP` as rendered by the
// page's date formatter (en-US, "MMM d, yyyy"). Exported so story tests can
// assert against the new-path date without recomputing it inline.
export const MOCK_NEW_FILE_RESUME_UPLOAD_DATE_DISPLAY = 'Mar 20, 2024';
export const MOCK_FILE_RESUME_ID = 'file_resume_1';
export const MOCK_FILE_RESUME_NAME = 'Taylor_J_resume.pdf';
export const MOCK_FILE_RESUME_UPLOAD_TIMESTAMP = '1710936000000';
export const MOCK_FILE_RESUME_PDF_URL = '/static/mock-data/sample-resume.pdf';

/**
 * Build a `JobSeekerResumesPayload` mock for the dual-resume V2 path
 * (PROF-9839). When `includeFile` is `false`, returns an empty `resumes`
 * array so callers can simulate users with no uploaded file resume.
 *
 * The file values intentionally differ from the legacy `fileInformation`
 * mocks (see `MOCK_NEW_FILE_RESUME_*` vs `MOCK_FILE_RESUME_*`) so tests
 * can verify which source the page is reading from.
 */
export const buildMockJobSeekerResumesPayload = (includeFile = true) => ({
    __typename: 'JobSeekerResumesPayload' as const,
    resumes: includeFile
        ? [
              {
                  __typename: 'JobSeekerResumeBundle' as const,
                  resumeId: 'resume_bundle_1',
                  originalUploadedFile: {
                      __typename: 'JobSeekerResumeFile' as const,
                      metadata: {
                          __typename: 'JobSeekerProfileFile' as const,
                          id: MOCK_NEW_FILE_RESUME_ID,
                          fileName: MOCK_NEW_FILE_RESUME_NAME,
                          fileType: 'RESUME' as const,
                          uploadTimestamp: MOCK_NEW_FILE_RESUME_UPLOAD_TIMESTAMP,
                      },
                  },
                  pdfResumeFile: {
                      __typename: 'JobSeekerResumeFile' as const,
                      url: MOCK_NEW_FILE_RESUME_PDF_URL,
                  },
              },
          ]
        : [],
});

const buildMockDualResumesEnabledPayload = () => ({
    __typename: 'JobSeekerProfileDualResumesEnabledPayload',
    enabled: MOCK_DUAL_RESUMES_ENABLED,
});

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Simulate realistic network delay for development
 */
const simulateNetworkDelay = (minMs = 200, maxMs = 800): Promise<void> => {
    const delay = Math.random() * (maxMs - minMs) + minMs;
    return new Promise((resolve) => setTimeout(resolve, delay));
};

/**
 * Get fresh state data from localStorage database
 */
const getFreshStateData = (nodeId?: string) => {
    const id = nodeId || 'apply_state_123';
    return querySmartApplyState(id);
};

/**
 * Create standardized query response
 */

const createQueryResponse = (data: any) => ({ data });

/**
 * Create standardized SmartApplyApplicationState response
 */

const createSmartApplyStateResponse = (nodeId: string, overrides: any = {}) => {
    const freshState = getFreshStateData(nodeId);
    return {
        __typename: 'SmartApplyApplicationState',
        id: nodeId,
        draftId: freshState?.draftId || 'light_apply_draft_456',
        ...overrides,
    };
};

/**
 * Create contact info from database or fallback
 */
const createContactInfoResponse = (nodeId: string, fields?: string[]) => {
    const freshState = getFreshStateData(nodeId);

    const contactInfo: any = freshState?.contactInfo || {};

    // If specific fields requested, return only those fields with proper structure
    if (fields) {
        const filteredInfo: any = { __typename: 'ContactInfoSmartApplyApplicationState' };
        fields.forEach((field) => {
            // Include field from localStorage data, preserving null/undefined properly
            filteredInfo[field] = Object.prototype.hasOwnProperty.call(contactInfo, field)
                ? contactInfo[field]
                : null;
        });

        console.log(
            `🔍 MockLink: createContactInfoResponse for fields [${fields.join(', ')}]:`,
            filteredInfo,
        );
        return filteredInfo;
    }

    // Return full contact info with sensible defaults
    return {
        __typename: 'ContactInfoSmartApplyApplicationState',
        firstName: contactInfo.firstName || '',
        lastName: contactInfo.lastName || '',
        email: contactInfo.email || '',
        phoneNumber: contactInfo.phoneNumber || '',
        dateOfBirth: contactInfo.dateOfBirth || '',
        gender: contactInfo.gender || '',
        area: contactInfo.area || '',
        city: contactInfo.city || '',
        country: contactInfo.country || 'JP',
        firstNamePronunciation: contactInfo.firstNamePronunciation || '',
        lastNamePronunciation: contactInfo.lastNamePronunciation || '',
    };
};

/**
 * Handle query operations with standardized pattern
 */
const handleQuery = async (
    observer: any,
    queryName: string,
    dataFactory: () => any,
    delay = [400, 800],
) => {
    try {
        await simulateNetworkDelay(delay[0], delay[1]);
        const responseData = dataFactory();
        observer.next(createQueryResponse(responseData));
        observer.complete();
    } catch (error) {
        console.error(`❌ MockLink: Error in ${queryName}:`, error);
        observer.error(error);
    }
};

/**
 * Handle mutation operations with standardized pattern
 */
const handleMutation = async (
    observer: any,
    mutationName: string,
    mutationFn: () => any,
    delay = [600, 1200],
) => {
    try {
        await simulateNetworkDelay(delay[0], delay[1]);
        const responseData = mutationFn();
        observer.next(createQueryResponse(responseData));
        observer.complete();
    } catch (error) {
        console.error(`❌ MockLink: Error in ${mutationName}:`, error);
        observer.error(error);
    }
};

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

interface UserProfile {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: {
        street: string;
        city: string;
        state: string;
        zipCode: string;
        country: string;
    };
    createdAt: string;
    updatedAt: string;
}

interface MockJobDescription {
    id: string;
    title: string;
    company: string;
    location: string;
    description: string;
    requirements: string[];
    benefits: string[];
    employmentType: string;
    salary?: {
        min: number;
        max: number;
        currency: string;
        period: string;
    };
}

// =============================================================================
// MOCK DATA
// =============================================================================

// Mutable mock state for auto-apply mode preference
let mockAutoApplyMode: string | null = null;
// Mutable mock state for SQ prefill preference
let mockSqPrefillPreference: string = 'OPT_IN';
// Mutable account-level DQ prefill consent. Null means no consent record (defaults OFF in UI).
let mockDqPrefillConsent: boolean | null = null;
// Mutable mock state for the selected resume type persisted by the Review variant's
// Update action (IAFE-2079 via the IAFE-2080 variant). Null until the variant persists it.
let mockSelectedResumeType: string | null = null;

const mockUserProfile: UserProfile = {
    id: 'user_123',
    firstName: 'Ichiko',
    lastName: 'Yuzawa',
    email: 'ichiko.yuzawa@email.com',
    phone: '+1 (512) 555-0123',
    address: {
        street: '123 Main St',
        city: 'Austin',
        state: 'TX',
        zipCode: '78701',
        country: 'US',
    },
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
};

const mockJobDescription: MockJobDescription = {
    id: 'job_abc123',
    title: '店舗スタッフ',
    company: '株式会社エービーシー',
    location: '東京都 中央区 日本橋',
    description: '顧客サービスと店舗運営を担当する店舗スタッフを募集しています。',
    requirements: ['カスタマーサービス経験', 'チームワーク', '日本語ネイティブレベル'],
    benefits: ['健康保険', '有給休暇', '交通費支給', '研修制度'],
    employmentType: 'パートタイム',
    salary: {
        min: 1000,
        max: 1500,
        currency: 'JPY',
        period: 'hourly',
    },
};

// =============================================================================
// MAIN MOCKLINK CLASS
// =============================================================================

export const createMockLink = (): ApolloLink => {
    return new ApolloLink((operation: Operation) => {
        return new Observable((observer) => {
            const { query, variables, operationName } = operation;
            const queryString = print(query);

            // =============================================================================
            // CORE USER & PROFILE OPERATIONS
            // =============================================================================

            if (queryString.includes('GetUserProfile')) {
                void handleQuery(observer, 'GetUserProfile', () => ({
                    userProfile: mockUserProfile,
                }));
            } else if (queryString.includes('UpdateUserProfile')) {
                void handleMutation(observer, 'UpdateUserProfile', () => ({
                    updateUserProfile: {
                        ...mockUserProfile,
                        ...variables?.input,
                        updatedAt: new Date().toISOString(),
                    },
                }));
            }

            // =============================================================================
            // JOB & APPLICATION OPERATIONS
            // =============================================================================
            else if (queryString.includes('GetJobDescription')) {
                void handleQuery(observer, 'GetJobDescription', () => ({
                    job: mockJobDescription,
                }));
            } else if (queryString.includes('GetJobInfo')) {
                void handleQuery(observer, 'GetJobInfo', () => ({
                    jobInfo: {
                        id: variables?.jobKey || 'default_job',
                        title: '店舗スタッフ',
                        company: '株式会社エービーシー',
                        location: '東京都 中央区 日本橋',
                    },
                }));
            } else if (queryString.includes('GetJobDetails')) {
                void handleQuery(observer, 'GetJobDetails', () => ({
                    jobDetails: mockJobDescription,
                }));
                // Match by operation name, not substring: GetAutoApplyScreenerQuestions also
                // references FindReviewBeforeSubmitDraft (as a field + input type), so a substring
                // check here would wrongly intercept it before its own branch below.
            } else if (operationName === 'FindReviewBeforeSubmitDraft') {
                void handleQuery(observer, 'FindReviewBeforeSubmitDraft', () => ({
                    findReviewBeforeSubmitDraft: {
                        __typename: 'FindReviewBeforeSubmitDraftPayload',
                        draft: {
                            __typename: 'AutoApplyDraft',
                            applicationDraftId: variables?.input?.applicationDraftId || '',
                            iaUid: 'mock-ia-uid',
                            state: 'CONTINUE',
                            lastPreviewId: null,
                            locale: 'en-US',
                            proctors: '',
                            jobTitle: 'Customer Support Team Lead',
                            jobLocation: 'Austin, TX',
                            employerName: 'BrightConnect Solutions',
                            applyableJob: {
                                __typename: 'ReviewBeforeSubmitApplyableJob',
                                jobId: 'mock-job-id',
                                clientJobRef: 'mock-client-job-ref',
                            },
                        },
                    },
                }));
            }

            // =============================================================================
            // SMART APPLY STATE OPERATIONS
            // =============================================================================
            else if (queryString.includes('GetSmartApplyApplicationState')) {
                void handleQuery(observer, 'GetSmartApplyApplicationState', () => {
                    const nodeId = variables?.nodeId || 'apply_state_123';
                    return {
                        smartApplyApplicationState: createSmartApplyStateResponse(nodeId, {
                            contactInfo: createContactInfoResponse(nodeId),
                        }),
                    };
                });
            } else if (queryString.includes('GetJobSeekerProfile')) {
                void handleQuery(observer, 'GetJobSeekerProfile', () => {
                    const profile = queryJobSeekerProfile();
                    return { jobSeekerProfile: profile };
                });
            } else if (queryString.includes('GetJobSeekerAccountKey')) {
                void handleQuery(observer, 'GetJobSeekerAccountKey', () => {
                    return {
                        jobSeekerProfile: {
                            __typename: 'JobSeekerProfilePayload',
                            profile: {
                                __typename: 'JobSeekerProfile',
                                accountKey: 'mock_account_key_12345',
                            },
                        },
                    };
                });
            }

            // =============================================================================
            // LIGHT APPLY SPECIFIC OPERATIONS
            // =============================================================================

            // Contact Info Queries
            else if (queryString.includes('GetNameFormData')) {
                void handleQuery(observer, 'GetNameFormData', () => {
                    const nodeId = variables?.nodeId || 'apply_state_123';
                    return {
                        smartApplyApplicationState: createSmartApplyStateResponse(nodeId, {
                            contactInfo: createContactInfoResponse(nodeId, [
                                'firstName',
                                'lastName',
                                'firstNamePronunciation',
                                'lastNamePronunciation',
                            ]),
                        }),
                        jobSeekerProfile: queryJobSeekerProfile(),
                    };
                });
            } else if (queryString.includes('GetContactDetailsFormData')) {
                void handleQuery(observer, 'GetContactDetailsFormData', () => {
                    const nodeId = variables?.nodeId || 'apply_state_123';
                    return {
                        smartApplyApplicationState: createSmartApplyStateResponse(nodeId, {
                            contactInfo: createContactInfoResponse(nodeId, [
                                'email',
                                'phoneNumber',
                            ]),
                        }),
                        jobSeekerProfile: queryJobSeekerProfile(),
                    };
                });
            } else if (queryString.includes('GetDateOfBirthFormData')) {
                void handleQuery(observer, 'GetDateOfBirthFormData', () => {
                    const nodeId = variables?.nodeId || 'apply_state_123';

                    // PROPER ARCHITECTURE: Incremental field caching with localStorage sync
                    const contactInfoResponse = createContactInfoResponse(nodeId, ['dateOfBirth']);
                    console.log(
                        `🔄 MockLink: GetDateOfBirthFormData - Retrieved from localStorage:`,
                        contactInfoResponse,
                    );

                    const response = {
                        smartApplyApplicationState: createSmartApplyStateResponse(nodeId, {
                            contactInfo: contactInfoResponse,
                        }),
                    };

                    console.log(`📤 MockLink: GetDateOfBirthFormData - Final response:`, response);
                    return response;
                });
            } else if (queryString.includes('GetGenderFormData')) {
                void handleQuery(observer, 'GetGenderFormData', () => {
                    const nodeId = variables?.nodeId || 'apply_state_123';
                    return {
                        smartApplyApplicationState: createSmartApplyStateResponse(nodeId, {
                            contactInfo: createContactInfoResponse(nodeId, ['gender']),
                        }),
                        jobSeekerProfile: queryJobSeekerProfile(),
                    };
                });
            } else if (queryString.includes('GetAddressFormData')) {
                void handleQuery(observer, 'GetAddressFormData', () => {
                    const nodeId = variables?.nodeId || 'apply_state_123';
                    return {
                        smartApplyApplicationState: createSmartApplyStateResponse(nodeId, {
                            contactInfo: createContactInfoResponse(nodeId, [
                                'area',
                                'city',
                                'country',
                            ]),
                        }),
                        jobSeekerProfile: queryJobSeekerProfile(),
                    };
                });
            }

            // Contact Info Mutations
            else if (queryString.includes('UpdateLightApplyNameInfo')) {
                void handleMutation(observer, 'UpdateLightApplyNameInfo', () => {
                    const nodeId = variables?.input?.id || 'apply_state_123';
                    const updateInput = variables?.updateContactInfoInput || {};

                    const result = mutateContactInfo(nodeId, updateInput);
                    return {
                        smartApplyApplicationState: {
                            updateContactInfo: {
                                state: result.contactInfo,
                                status: { updateProfile: true },
                            },
                        },
                    };
                });
            } else if (queryString.includes('UpdateLightApplyContactDetails')) {
                void handleMutation(observer, 'UpdateLightApplyContactDetails', () => {
                    const nodeId = variables?.input?.id || 'apply_state_123';
                    const updateInput = variables?.updateContactInfoInput || {};

                    const result = mutateContactInfo(nodeId, updateInput);
                    return {
                        smartApplyApplicationState: {
                            updateContactInfo: {
                                state: result.contactInfo,
                                status: { updateProfile: true },
                            },
                        },
                    };
                });
            } else if (queryString.includes('UpdateLightApplyDateOfBirth')) {
                void handleMutation(observer, 'UpdateLightApplyDateOfBirth', () => {
                    const nodeId = variables?.input?.id || 'apply_state_123';
                    const updateInput = variables?.updateContactInfoInput || {};

                    const result = mutateContactInfo(nodeId, updateInput);
                    return {
                        smartApplyApplicationState: {
                            updateContactInfo: {
                                state: result.contactInfo,
                                status: { updateProfile: true },
                            },
                        },
                    };
                });
            } else if (queryString.includes('UpdateLightApplyGenderInfo')) {
                void handleMutation(observer, 'UpdateLightApplyGenderInfo', () => {
                    const nodeId = variables?.input?.id || 'apply_state_123';
                    const updateInput = variables?.updateContactInfoInput || {};

                    const result = mutateContactInfo(nodeId, updateInput);
                    return {
                        smartApplyApplicationState: {
                            updateContactInfo: {
                                state: result.contactInfo,
                                status: { updateProfile: true },
                            },
                        },
                    };
                });
            } else if (queryString.includes('UpdateLightApplyAddress')) {
                void handleMutation(observer, 'UpdateLightApplyAddress', () => {
                    const nodeId = variables?.input?.id || 'apply_state_123';
                    const updateContactInfoInput = variables?.updateContactInfoInput || {};

                    // Update SmartApplyApplicationState contactInfo
                    const contactResult = mutateContactInfo(nodeId, updateContactInfoInput);

                    // Note: For now, focus on SmartApplyApplicationState updates
                    // JobSeekerProfile location updates are handled by the frontend cache

                    return {
                        smartApplyApplicationState: {
                            updateContactInfo: {
                                state: contactResult.contactInfo,
                                status: { updateProfile: true },
                            },
                        },
                    };
                });
            }

            // Resume Operations
            else if (queryString.includes('GetResumeFormData')) {
                void handleQuery(observer, 'GetResumeFormData', () => {
                    const nodeId = variables?.nodeId || 'apply_state_123';
                    const freshState = getFreshStateData(nodeId);
                    const resume: any = freshState?.resume || {};

                    return {
                        smartApplyApplicationState: createSmartApplyStateResponse(nodeId, {
                            resume: {
                                __typename: 'ResumeSmartApplyApplicationState',
                                resumeDetail: {
                                    __typename: 'ResumeDetailSmartApplyApplicationState',
                                    resumeChoice: resume.resumeChoice || 'CREATE_NEW',
                                },
                            },
                        }),
                    };
                });
            } else if (queryString.includes('UpdateLightApplyResume')) {
                void handleMutation(observer, 'UpdateLightApplyResume', () => {
                    const nodeId = variables?.input?.id || 'apply_state_123';
                    const resumeChoice = variables?.updateResumeInput?.resumeChoice;

                    const result = mutateResume(nodeId, { resumeChoice } as any);
                    return {
                        smartApplyApplicationState: {
                            updateResume: {
                                state: result.resume,
                                status: { success: true },
                            },
                        },
                    };
                });
            }

            // Screener Questions Operations
            else if (queryString.includes('GetScreenerQuestionsData')) {
                void handleQuery(observer, 'GetScreenerQuestionsData', () => {
                    const nodeId = variables?.nodeId || 'apply_state_123';
                    const filterInput = variables?.filterInput || {};
                    const applicationDraftId = filterInput.applicationDraftId || 'apply_draft_123';

                    // 📖 Fetch fresh data from localStorage database for SmartApplyApplicationState
                    const freshState = getFreshStateData(nodeId);

                    // 🎭 Mock findApplicationDrafts with rich question structure
                    const findApplicationDraftsMock = {
                        __typename: 'FindApplicationDraftsConnection',
                        operationDetails: [
                            {
                                __typename: 'DraftManagementApplicationDraft',
                                applicationDraftId: applicationDraftId,
                                questionsAndAnswers: {
                                    __typename: 'DraftManagementQuestionsAndAnswers',
                                    screener: [
                                        {
                                            __typename: 'DraftManagementQuestionAndAnswer',
                                            question: {
                                                __typename: 'DraftManagementQuestion',
                                                questionId: 'education',
                                                type: 'SELECT',
                                                indeedTaxonomyParameters: [],
                                                qualification: null,
                                                details: {
                                                    __typename: 'DraftManagementSelectQuestion',
                                                    label: '最終学歴を選択してください',
                                                    condition: null,
                                                    requirement: 'REQUIRED',
                                                    options: [
                                                        {
                                                            __typename:
                                                                'DraftManagementQuestionOption',
                                                            value: 'high-school',
                                                            label: '高等学校卒業',
                                                        },
                                                        {
                                                            __typename:
                                                                'DraftManagementQuestionOption',
                                                            value: 'vocational',
                                                            label: '専門学校卒業',
                                                        },
                                                        {
                                                            __typename:
                                                                'DraftManagementQuestionOption',
                                                            value: 'university',
                                                            label: '大学卒業',
                                                        },
                                                        {
                                                            __typename:
                                                                'DraftManagementQuestionOption',
                                                            value: 'graduate',
                                                            label: '大学院卒業',
                                                        },
                                                    ],
                                                },
                                            },
                                            answer: null, // No fallback answer from findApplicationDrafts
                                            suggestedAnswer: {
                                                __typename: 'DraftManagementTextSuggestedAnswer',
                                                questionViewTk: 'education_suggestion_token',
                                                suggestion: {
                                                    __typename: 'DraftManagementSuggestion',
                                                    value: 'university',
                                                },
                                            },
                                        },
                                        {
                                            __typename: 'DraftManagementQuestionAndAnswer',
                                            question: {
                                                __typename: 'DraftManagementQuestion',
                                                questionId: 'commute-available',
                                                type: 'SELECT',
                                                indeedTaxonomyParameters: [],
                                                qualification: null,
                                                details: {
                                                    __typename: 'DraftManagementSelectQuestion',
                                                    label: '勤務地までの通勤は可能ですか？',
                                                    condition: null,
                                                    requirement: 'REQUIRED',
                                                    options: [
                                                        {
                                                            __typename:
                                                                'DraftManagementQuestionOption',
                                                            value: 'はい',
                                                            label: 'はい',
                                                        },
                                                        {
                                                            __typename:
                                                                'DraftManagementQuestionOption',
                                                            value: 'いいえ',
                                                            label: 'いいえ',
                                                        },
                                                    ],
                                                },
                                            },
                                            answer: null, // No fallback answer from findApplicationDrafts
                                            suggestedAnswer: {
                                                __typename: 'DraftManagementTextSuggestedAnswer',
                                                questionViewTk: 'commute_suggestion_token',
                                                suggestion: {
                                                    __typename: 'DraftManagementSuggestion',
                                                    value: 'はい',
                                                },
                                            },
                                        },
                                    ],
                                },
                            },
                        ],
                    };

                    // Transform screener answers to include proper GraphQL __typename
                    const questionsAndAnswers = freshState?.questionsAndAnswers || {
                        __typename: 'ScreenerQuestionsAndAnswersSmartApplyApplicationState',
                        screener: [],
                    };

                    // Ensure each screener answer has proper GraphQL structure
                    if (
                        questionsAndAnswers.screener &&
                        Array.isArray(questionsAndAnswers.screener)
                    ) {
                        questionsAndAnswers.screener = questionsAndAnswers.screener.map(
                            (item: any) => ({
                                __typename: 'ScreenerQuestionsSmartApplyApplicationState',
                                questionId: item.questionId,
                                answer: item.answer
                                    ? {
                                          __typename: 'TextAnswerSmartApplyApplicationState',
                                          value: item.answer.value,
                                      }
                                    : null,
                            }),
                        ) as any;
                    }

                    return {
                        // Question structure and content from findApplicationDrafts
                        findApplicationDrafts: findApplicationDraftsMock,
                        // User's saved answers from SmartApplyApplicationState (takes priority)
                        smartApplyApplicationState: createSmartApplyStateResponse(nodeId, {
                            questionsAndAnswers,
                        }),
                    };
                });
            } else if (queryString.includes('UpdateLightApplyScreenerQuestions')) {
                void handleMutation(observer, 'UpdateLightApplyScreenerQuestions', () => {
                    const nodeId = variables?.input?.id || 'apply_state_123';
                    const updateInput = variables?.updateQuestionsInput || {};

                    // 💾 Save to localStorage database using proper mutation function
                    const result = mutateQuestionsAndAnswers(nodeId, {
                        screener: updateInput.screener || [],
                    });

                    return {
                        smartApplyApplicationState: {
                            __typename: 'SmartApplyApplicationStateMutations',
                            updateScreenerQuestionAnswers: {
                                __typename: 'UpdateScreenerQuestionAnswersPayload',
                                payload: result.questionsAndAnswers,
                            },
                        },
                    };
                });
            } else if (queryString.includes('SaveSQApplicationData')) {
                // Auto Apply screener-answer save (SaveSQApplicationData). Persists to the
                // localStorage database and echoes an output-shaped payload so Apollo can
                // normalize the answer union against the mutation's selection set.
                void handleMutation(observer, 'SaveSQApplicationData', () => {
                    const nodeId = variables?.draftInput?.id || 'apply_state_123';
                    const inputScreener = variables?.sqInput?.screener || [];

                    const toOutputAnswer = (answer: any) => {
                        if (!answer) return null;
                        if (answer.textAnswer) {
                            return {
                                __typename: 'TextAnswerSmartApplyApplicationState',
                                value: answer.textAnswer.value,
                            };
                        }
                        if (answer.multiTextAnswer) {
                            return {
                                __typename: 'MultiTextAnswerSmartApplyApplicationState',
                                valueList: (answer.multiTextAnswer.valueList || []).map(
                                    (v: any) => ({
                                        __typename: 'TextAnswerSmartApplyApplicationState',
                                        value: v.value,
                                    }),
                                ),
                            };
                        }
                        if (answer.hierarchicalAnswer) {
                            return {
                                __typename: 'HierarchicalAnswerSmartApplyApplicationState',
                                valueList: (answer.hierarchicalAnswer.valueList || []).map(
                                    (v: any) => ({
                                        __typename:
                                            'HierarchicalAnswerValueSmartApplyApplicationState',
                                        id: v.id,
                                        value: v.value,
                                    }),
                                ),
                            };
                        }
                        const toFile = (uri: string) => ({
                            __typename: 'FileSmartApplyApplicationState',
                            name: uri,
                            fileToken: uri,
                            contentType: '',
                            contentHash: '',
                            downloadHash: '',
                        });
                        if (answer.multiFileAnswer) {
                            return {
                                __typename: 'MultiFileAnswerSmartApplyApplicationState',
                                files: (answer.multiFileAnswer.files || []).map((f: any) => ({
                                    __typename: 'FileAnswerSmartApplyApplicationState',
                                    file: toFile(f.file?.uri ?? ''),
                                })),
                            };
                        }
                        if (answer.fileAnswer) {
                            return {
                                __typename: 'FileAnswerSmartApplyApplicationState',
                                file: toFile(answer.fileAnswer.file?.uri ?? ''),
                            };
                        }
                        return null;
                    };

                    // Persist the output-shaped screener so the GetAutoApplyScreenerQuestions load
                    // branch can return it verbatim and saved answers reappear after a reload.
                    const outputScreener = inputScreener.map((item: any) => ({
                        __typename: 'QuestionAndAnswerSmartApplyApplicationState',
                        questionId: item.questionId,
                        answer: toOutputAnswer(item.answer),
                    }));
                    mutateQuestionsAndAnswers(nodeId, { screener: outputScreener });

                    return {
                        smartApplyApplicationState: {
                            __typename: 'SmartApplyApplicationStateMutations',
                            updateScreenerQuestionAnswers: {
                                __typename:
                                    'UpdateScreenerQuestionAnswersSmartApplyApplicationState',
                                payload: {
                                    __typename:
                                        'ScreenerQuestionsAndAnswersSmartApplyApplicationState',
                                    _id: nodeId,
                                    screener: outputScreener,
                                },
                            },
                        },
                    };
                });
            }

            // =============================================================================
            // GENERIC/FALLBACK OPERATIONS
            // =============================================================================

            // Generic UpdateContactInfo (excluding Light Apply specific ones)
            else if (
                queryString.includes('UpdateContactInfo') &&
                !queryString.includes('UpdateLightApply') &&
                !queryString.includes('UpdateSmartApply')
            ) {
                void handleMutation(observer, 'UpdateContactInfo', () => {
                    const nodeId = variables?.input?.id || 'apply_state_123';
                    const updateInput = variables?.updateContactInfoInput || {};

                    const result = mutateContactInfo(nodeId, updateInput);
                    return {
                        smartApplyApplicationState: {
                            updateContactInfo: {
                                state: result.contactInfo,
                                status: { updateProfile: true },
                            },
                        },
                    };
                });
            }

            // =============================================================================
            // AUTO APPLY SETUP - AUTOMATION LEVEL OPERATIONS
            // =============================================================================
            else if (queryString.includes('GetAutoApplyMode')) {
                void handleQuery(observer, 'GetAutoApplyMode', () => {
                    return {
                        autoApplyInfo: {
                            __typename: 'AutoApplyInfoPayload',
                            mode: mockAutoApplyMode,
                        },
                    };
                });
            } else if (queryString.includes('UpdateAutoApplyMode')) {
                void handleMutation(observer, 'UpdateAutoApplyMode', () => {
                    const inputMode = variables?.input?.mode;
                    if (inputMode) {
                        mockAutoApplyMode = inputMode;
                    }
                    return {
                        updateAutoApply: {
                            __typename: 'UpdateAutoApplyPayload',
                            mode: mockAutoApplyMode,
                        },
                    };
                });
            } else if (queryString.includes('GetSqPrefillPreference')) {
                void handleQuery(observer, 'GetSqPrefillPreference', () => {
                    return {
                        autoApplyInfo: {
                            __typename: 'AutoApplyInfoPayload',
                            sqPastAnswerPrefillPreference: mockSqPrefillPreference,
                        },
                    };
                });
            } else if (queryString.includes('UpdateSqPrefillPreference')) {
                void handleMutation(observer, 'UpdateSqPrefillPreference', () => {
                    const inputPreference = variables?.input?.sqPastAnswerPrefillPreference;
                    if (inputPreference) {
                        mockSqPrefillPreference = inputPreference;
                    }
                    return {
                        updateAutoApply: {
                            __typename: 'UpdateAutoApplyPayload',
                            sqPastAnswerPrefillPreference: mockSqPrefillPreference,
                        },
                    };
                });
            } else if (
                queryString.includes('SetDqPrefillConsent') ||
                queryString.includes('SetPastAnswersDqPrefillConsent')
            ) {
                const operationName = queryString.includes('SetPastAnswersDqPrefillConsent')
                    ? 'SetPastAnswersDqPrefillConsent'
                    : 'SetDqPrefillConsent';
                void handleMutation(observer, operationName, () => {
                    mockDqPrefillConsent = variables?.consent === true;
                    return {
                        setDqPrefillConsent: {
                            __typename: 'SetDqPrefillConsentPayload',
                            consent: mockDqPrefillConsent,
                        },
                    };
                });
            } else if (queryString.includes('UpdateAutoApplyFromReview')) {
                // Activation and the progressive-automation Review variant's Update
                // button both go through this mutation (ReviewPage.graphql): activation
                // sends autoApplyStatus (+ the SQ default for new users); the variant
                // sends the SQ preference. DQ consent uses SetDqPrefillConsent above.
                void handleMutation(observer, 'UpdateAutoApplyFromReview', () => {
                    const inputStatus = variables?.input?.autoApplyStatus;
                    const inputSqPreference = variables?.input?.sqPastAnswerPrefillPreference;
                    const inputResumeType = variables?.input?.selectedResumeType;
                    if (inputSqPreference) {
                        mockSqPrefillPreference = inputSqPreference;
                    }
                    // Echo back the persisted resume type so the ReviewPage ITV check
                    // passes when the variant persists a pre-filled resume type.
                    if (inputResumeType) {
                        mockSelectedResumeType = inputResumeType;
                    }
                    return {
                        updateAutoApply: {
                            __typename: 'UpdateAutoApplyPayload',
                            autoApplyStatus: inputStatus ?? null,
                            selectedResumeType: mockSelectedResumeType,
                            sqPastAnswerPrefillPreference: mockSqPrefillPreference,
                        },
                    };
                });
            }

            // =============================================================================
            // AUTO APPLY SETUP - PAST ANSWERS OPERATIONS
            // =============================================================================
            else if (queryString.includes('GetAutoApplyEligiblePastAnswers')) {
                void handleQuery(observer, 'GetAutoApplyEligiblePastAnswers', () => {
                    const first = (variables?.first as number) || 10;
                    const after = (variables?.after as string) || null;
                    return {
                        autoApplyEligiblePastAnswers: createMockPastAnswersConnection(
                            comprehensiveMockPastAnswers,
                            first,
                            after,
                        ),
                    };
                });
            } else if (queryString.includes('GetEligiblePastDqAnswers')) {
                void handleQuery(observer, 'GetEligiblePastDqAnswers', () => {
                    const first = (variables.first as number) || 10;
                    const after = (variables.after as string) || null;
                    const dqConnection = createMockPastAnswersConnection(
                        mockDqPrefillConsent ? comprehensiveMockPastAnswers : [],
                        first,
                        after,
                    );
                    return {
                        eligiblePastDQAnswers: {
                            __typename: 'EligiblePastDQAnswersConnection',
                            consent: mockDqPrefillConsent,
                            edges: dqConnection.edges.map((edge) => ({
                                __typename: 'EligiblePastDQAnswerEdge',
                                cursor: edge.cursor,
                                node: {
                                    __typename: 'EligiblePastDQAnswer',
                                    intentId: edge.node.question.intentId,
                                    question: {
                                        __typename: 'EligiblePastDQQuestion',
                                        id: edge.node.question.id,
                                        text: edge.node.question.question,
                                        type:
                                            edge.node.question.type === 'QUESTION_BANK'
                                                ? 'UNKNOWN'
                                                : edge.node.question.type,
                                        options: edge.node.question.options.map((option) => ({
                                            __typename: 'EligiblePastDQQuestionOption',
                                            value: option.value,
                                            label: option.label,
                                        })),
                                    },
                                    answer: {
                                        __typename: 'EligiblePastDQAnswerValue',
                                        values: edge.node.answers[0]?.values ?? [],
                                    },
                                    domain: 'Personal information',
                                    updatedAt: edge.node.answers[0]?.createdAt ?? null,
                                },
                            })),
                            pageInfo: dqConnection.pageInfo,
                            totalCount: dqConnection.totalCount,
                        },
                    };
                });
            }

            // =============================================================================
            // AUTO APPLY SETUP - RESUME OPERATIONS
            // =============================================================================
            else if (queryString.includes('GetAutoApplyResumes')) {
                console.log('🔍 MockLink: Handling GetAutoApplyResumes query');
                void handleQuery(observer, 'GetAutoApplyResumes', () => {
                    return {
                        // Dual-resume migration (PROF-9839). See
                        // `MOCK_DUAL_RESUMES_ENABLED` at the top of this file.
                        jobSeekerProfileDualResumesEnabled: buildMockDualResumesEnabledPayload(),
                        jobSeekerResumes: buildMockJobSeekerResumesPayload(),
                        autoApplyInfo: {
                            __typename: 'AutoApplyInfoPayload',
                            selectedResumeType: null,
                            sqPastAnswerPrefillPreference: mockSqPrefillPreference,
                        },
                        jobSeekerProfile: {
                            __typename: 'JobSeekerProfilePayload',
                            profile: {
                                __typename: 'JobSeekerProfile',
                                // Most recent apply, used to pre-fill the resume
                                // selection for progressive-automation entries (IAFE-2079).
                                latestApply: {
                                    __typename: 'JobSeekerProfileLatestApplyPayload',
                                    resumeType: 'INDEED_RESUME',
                                },
                                // Legacy file-based resumes (usually at most one).
                                // Read on the legacy path
                                // (MOCK_DUAL_RESUMES_ENABLED === false).
                                fileInformation: [
                                    {
                                        __typename: 'JobSeekerProfileFile',
                                        id: MOCK_FILE_RESUME_ID,
                                        fileName: MOCK_FILE_RESUME_NAME,
                                        fileType: 'RESUME',
                                        uploadTimestamp: MOCK_FILE_RESUME_UPLOAD_TIMESTAMP,
                                        standardHashCode: 'abc123def456',
                                    },
                                ],
                                // Indeed Resume data (filter for INDEED_RESUME type)
                                resume: [
                                    {
                                        __typename: 'JobSeekerProfileResume',
                                        id: 'indeed_resume_1',
                                        resumeType: 'INDEED_RESUME',
                                        dateModified: '2025-03-20T10:30:00Z',
                                        firstName: 'Janett',
                                        firstNameFurigana: null,
                                        lastName: 'Smith',
                                        lastNameFurigana: null,
                                        phoneNumber: '(512) 234-5678',
                                        wordCount: 450,
                                        location: {
                                            __typename: 'JobSeekerProfileLocationType',
                                            formattedLocation: 'Austin, TX',
                                            unknownLocation: null,
                                            postalCode: '78701',
                                        },
                                        educations: [
                                            {
                                                __typename: 'JobSeekerProfileEducation',
                                                school: 'University of Texas at Austin',
                                                degree: [
                                                    {
                                                        __typename: 'JobSeekerProfileDegree',
                                                        degreeName: 'Bachelor of Science',
                                                        field: 'Computer Science',
                                                    },
                                                ],
                                            },
                                        ],
                                        workExperiences: [
                                            {
                                                __typename: 'JobSeekerProfileWorkExperience',
                                                title: 'Senior Customer Service Representative',
                                                company: 'BrightPath Solutions',
                                            },
                                            {
                                                __typename: 'JobSeekerProfileWorkExperience',
                                                title: 'Customer Support Specialist',
                                                company: 'EcoWave Electronics',
                                            },
                                            {
                                                __typename: 'JobSeekerProfileWorkExperience',
                                                title: 'Call Center Representative',
                                                company: 'TechConnect Inc.',
                                            },
                                        ],
                                    },
                                ],
                            },
                        },
                    };
                });
            }

            // Download File Resume Mutation
            else if (queryString.includes('DownloadFileResume')) {
                console.log('🔍 MockLink: Handling DownloadFileResume mutation');
                void handleMutation(observer, 'DownloadFileResume', () => {
                    // Return a local mock PDF URL to avoid CORS issues
                    // The PDF is served as a static file from packages/auto-apply-preference/dist/statics/
                    return {
                        createJobSeekerProfileResumeFileTempUrls: {
                            __typename: 'CreateJobSeekerProfileResumeFileTempUrlsPayload',
                            pdfResumeUrl: '/static/mock-data/sample-resume.pdf',
                        },
                    };
                });
            }

            // Create Upload Resume URL Mutation
            else if (queryString.includes('CreateUploadResumeUrl')) {
                console.log('🔍 MockLink: Handling CreateUploadResumeUrl mutation');
                void handleMutation(observer, 'CreateUploadResumeUrl', () => {
                    // In mock mode, return a fake upload URL
                    // The actual upload will be skipped in mock mode
                    return {
                        createJobSeekerProfileUploadResumeFileTempUrl: {
                            __typename: 'CreateJobSeekerProfileUploadResumeFileTempUrlPayload',
                            uploadURL: 'https://mock-s3-upload.example.com/resume',
                            fileId: `mock_file_${Date.now()}`,
                        },
                    };
                });
            }

            // Add Job Seeker Profile Resume File Mutation
            else if (queryString.includes('AddJobSeekerProfileResumeFile')) {
                console.log('🔍 MockLink: Handling AddJobSeekerProfileResumeFile mutation');
                void handleMutation(observer, 'AddJobSeekerProfileResumeFile', () => {
                    const newFileId = `file_resume_${Date.now()}`;

                    return {
                        addJobSeekerProfileResumeFile: {
                            __typename: 'AddJobSeekerProfileResumeFilePayload',
                            originalResumeFile: {
                                __typename: 'JobSeekerProfileFile',
                                id: newFileId,
                            },
                        },
                    };
                });
            }

            // Delete Resume Bundle Mutation (PROFPLAT-9559)
            // Dual-resume V2 cleanup: the Resume Page deletes any existing
            // RESUME-type bundle before adding a new one when the dual-resume
            // platform is enabled. Echo the requested resumeId back so the
            // mock honours the same shape the real `deleteResumeBundle`
            // mutation returns.
            else if (queryString.includes('DeleteResumeBundle')) {
                console.log('🔍 MockLink: Handling DeleteResumeBundle mutation');
                void handleMutation(observer, 'DeleteResumeBundle', () => {
                    const resumeId =
                        operation.variables?.input?.resumeId ??
                        (operation.variables as any)?.resumeId ??
                        null;
                    return {
                        deleteResumeBundle: {
                            __typename: 'DeleteResumeBundlePayload',
                            resumeId,
                        },
                    };
                });
            }

            // =============================================================================
            // AUTO APPLY SETUP - REVIEW PAGE OPERATIONS
            // =============================================================================
            else if (queryString.includes('GetAutoApplyReviewData')) {
                console.log('🔍 MockLink: Handling GetAutoApplyReviewData query');
                void handleQuery(observer, 'GetAutoApplyReviewData', () => {
                    return {
                        // Dual-resume migration (PROF-9839). See
                        // `MOCK_DUAL_RESUMES_ENABLED` at the top of this file.
                        jobSeekerProfileDualResumesEnabled: buildMockDualResumesEnabledPayload(),
                        jobSeekerResumes: buildMockJobSeekerResumesPayload(),
                        jobSeekerProfile: {
                            __typename: 'JobSeekerProfilePayload',
                            profile: {
                                __typename: 'JobSeekerProfile',
                                // Most recent apply, used to pre-fill the resume
                                // selection for progressive-automation entries (IAFE-2079).
                                latestApply: {
                                    __typename: 'JobSeekerProfileLatestApplyPayload',
                                    resumeType: 'INDEED_RESUME',
                                },
                                fileInformation: [
                                    {
                                        __typename: 'JobSeekerProfileFile',
                                        id: MOCK_FILE_RESUME_ID,
                                        fileName: MOCK_FILE_RESUME_NAME,
                                        fileType: 'RESUME',
                                        uploadTimestamp: MOCK_FILE_RESUME_UPLOAD_TIMESTAMP,
                                    },
                                ],
                                resume: [
                                    {
                                        __typename: 'JobSeekerProfileResume',
                                        id: 'indeed_resume_1',
                                        resumeType: 'INDEED_RESUME',
                                        dateModified: '2025-03-20T10:30:00Z',
                                        firstName: 'Janett',
                                        lastName: 'Smith',
                                        phoneNumber: '(512) 234-5678',
                                        headline: 'Senior customer service representative',
                                        summary:
                                            'Dedicated customer service professional with over 8 years of experience.',
                                        location: {
                                            __typename: 'JobSeekerProfileLocationType',
                                            formattedLocation: 'Austin, TX',
                                        },
                                        workExperiences: [
                                            {
                                                __typename: 'JobSeekerProfileWorkExperience',
                                                title: 'Senior Customer Service Representative',
                                                company: 'BrightPath Solutions',
                                                description: null,
                                                location: null,
                                                dateRange: null,
                                            },
                                        ],
                                        educations: [],
                                        skills: [],
                                        certifications: [],
                                        languageSkills: [],
                                    },
                                ],
                            },
                        },
                        autoApplyInfo: {
                            __typename: 'AutoApplyInfoPayload',
                            selectedResumeType: 'INDEED_RESUME',
                            autoApplyStatus: null,
                            mode: null,
                            sqPastAnswerPrefillPreference: mockSqPrefillPreference,
                        },
                        eligiblePastDQAnswers: {
                            __typename: 'EligiblePastDQAnswersConnection',
                            consent: mockDqPrefillConsent,
                        },
                        currentUser: {
                            __typename: 'CurrentUserPayload',
                            account: {
                                __typename: 'PassportAccount',
                                accountProfile: {
                                    __typename: 'AccountProfile',
                                    email: {
                                        __typename: 'Email',
                                        emailAddress: 'janett.smith@example.com',
                                    },
                                },
                            },
                        },
                    };
                });
            }

            // =============================================================================
            // AUTO APPLY SETUP - JOB PREFERENCES OPERATIONS
            // =============================================================================
            else if (
                queryString.includes('GetJobPreferences') ||
                queryString.includes('GetReviewJobPreferences') ||
                queryString.includes('GetMatchPreferences')
            ) {
                console.log('🔍 MockLink: Handling GetJobPreferences/GetMatchPreferences query');
                void handleQuery(observer, 'GetJobPreferences', () => {
                    return {
                        jobSeekerProfileStructuredData: {
                            __typename: 'JobSeekerProfileStructuredDataPayload',
                            preferences: [
                                {
                                    __typename: 'JobSeekerProfileStructuredDataPreference',
                                    id: 'preferences_1',
                                    jobTitles: [
                                        {
                                            __typename: 'JobSeekerProfileStructuredDataJobTitle',
                                            id: 'jt_1',
                                            jobTitle: 'Software Engineer',
                                        },
                                        {
                                            __typename: 'JobSeekerProfileStructuredDataJobTitle',
                                            id: 'jt_2',
                                            jobTitle: 'Full Stack Developer',
                                        },
                                    ],
                                    locations: [
                                        {
                                            __typename: 'JobSeekerProfileStructuredDataLocation',
                                            id: 'location_1',
                                            formattedLocationShort: 'Austin, TX',
                                        },
                                        {
                                            __typename: 'JobSeekerProfileStructuredDataLocation',
                                            id: 'location_2',
                                            formattedLocationShort: 'Denver, CO',
                                        },
                                    ],
                                    workAreas: [
                                        {
                                            __typename: 'JobSeekerProfileStructuredDataWorkArea',
                                            id: 'wa_1',
                                            workAreaTaxonomyConcept: {
                                                __typename: 'TaxonomyConcept',
                                                suid: 'wa_tech',
                                                label: 'Technology',
                                            },
                                            sentiment: 'POSITIVE',
                                        },
                                        {
                                            __typename: 'JobSeekerProfileStructuredDataWorkArea',
                                            id: 'wa_2',
                                            workAreaTaxonomyConcept: {
                                                __typename: 'TaxonomyConcept',
                                                suid: 'wa_finance',
                                                label: 'Finance',
                                            },
                                            sentiment: 'POSITIVE',
                                        },
                                    ],
                                    jobTypes: [
                                        {
                                            __typename: 'JobSeekerProfileStructuredDataJobType',
                                            id: 'jtype_1',
                                            jobType: 'Full-time',
                                            jobTypeTaxonomyConcept: {
                                                __typename: 'TaxonomyConcept',
                                                suid: 'jt_fulltime',
                                                label: 'Full-time',
                                            },
                                            sentiment: 'POSITIVE',
                                        },
                                    ],
                                    shifts: [
                                        {
                                            __typename: 'JobSeekerProfileStructuredDataShift',
                                            id: 'shift_1',
                                            shift: 'Day shift',
                                            shiftTaxonomyConcept: {
                                                __typename: 'TaxonomyConcept',
                                                suid: 'shift_day',
                                                label: 'Day shift',
                                            },
                                            sentiment: 'POSITIVE',
                                        },
                                        {
                                            __typename: 'JobSeekerProfileStructuredDataShift',
                                            id: 'shift_2',
                                            shift: 'Monday to Friday',
                                            shiftTaxonomyConcept: {
                                                __typename: 'TaxonomyConcept',
                                                suid: 'shift_m2f',
                                                label: 'Monday to Friday',
                                            },
                                            sentiment: 'POSITIVE',
                                        },
                                    ],
                                    minimumPay: {
                                        __typename: 'JobSeekerProfileStructuredDataMinimumPay',
                                        id: 'min_pay_1',
                                        amount: 60000,
                                        currency: 'USD',
                                    },
                                    relocation: {
                                        __typename: 'JobSeekerProfileStructuredDataRelocation',
                                        ableToRelocate: true,
                                    },
                                    maximumCommute: {
                                        __typename: 'JobSeekerProfileStructuredDataMaximumCommute',
                                        timeMinutes: 45,
                                    },
                                    attributes: [
                                        {
                                            __typename: 'JobSeekerProfileStructuredDataAttribute',
                                            suid: 'remote_work',
                                            label: 'Remote',
                                            sentiment: 'POSITIVE',
                                        },
                                        {
                                            __typename: 'JobSeekerProfileStructuredDataAttribute',
                                            suid: 'hybrid_work',
                                            label: 'Hybrid work',
                                            sentiment: 'POSITIVE',
                                        },
                                        {
                                            __typename: 'JobSeekerProfileStructuredDataAttribute',
                                            suid: 'in_person',
                                            label: 'In-person',
                                            sentiment: 'POSITIVE',
                                        },
                                    ],
                                },
                            ],
                        },
                    };
                });
            }

            // =============================================================================
            // GRAPHQL INTROSPECTION & DEVTOOLS
            // =============================================================================
            else if (
                queryString.includes('IntrospectionQuery') ||
                operation.operationName === 'IntrospectionQuery'
            ) {
                // Silently ignore introspection queries (used by Apollo DevTools)
                setTimeout(() => {
                    observer.next({
                        data: {
                            __schema: {
                                queryType: { name: 'Query' },
                                mutationType: { name: 'Mutation' },
                                subscriptionType: null,
                                types: [],
                            },
                        },
                    });
                    observer.complete();
                }, 100);
            }

            // =============================================================================
            // APOLLO DEVELOPMENT INFO SUPPORT
            // =============================================================================
            else if (queryString.includes('GetSmartApplyDevInfo')) {
                // Return the same SmartApplyApplicationState data that's used elsewhere
                // This ensures ApolloCacheInspector can monitor cache changes
                const nodeId = variables?.nodeId;

                void simulateNetworkDelay(300, 500).then(() => {
                    // Get current state from localStorage to ensure consistency (client-side only)
                    let smartApplyState = null;

                    if (typeof window !== 'undefined') {
                        try {
                            const storedData = localStorage.getItem('graphql-db:smartApplyStates');
                            if (storedData) {
                                const parsed = JSON.parse(storedData);
                                smartApplyState = parsed[nodeId];
                            }
                        } catch (error) {
                            console.warn(
                                '⚠️  MockLink: Could not access localStorage for GetSmartApplyDevInfo:',
                                error,
                            );
                        }
                    }

                    // Fallback to default state if not found
                    if (!smartApplyState) {
                        smartApplyState = {
                            __typename: 'SmartApplyApplicationState',
                            id: nodeId || 'apply_state_123',
                            draftId: 'draft_apply_state_123',
                            contactInfo: {
                                __typename: 'ContactInfoSmartApplyApplicationState',
                                firstName: null,
                                lastName: null,
                                firstNamePronunciation: null,
                                lastNamePronunciation: null,
                                email: null,
                                phoneNumber: null,
                                gender: null,
                                dateOfBirth: null,
                                city: null,
                                area: null,
                                country: null,
                            },
                            resume: {
                                __typename: 'ResumeSmartApplyApplicationState',
                                resumeDetail: null,
                                tailoredResume: null,
                            },
                            questionsAndAnswers: {
                                __typename: 'ScreenerQuestionsAndAnswersSmartApplyApplicationState',
                                screener: [],
                            },
                        };
                    }

                    observer.next({
                        data: {
                            smartApplyApplicationState: smartApplyState,
                        },
                    });
                    observer.complete();
                });
            }

            // =============================================================================
            // INDEX PAGE DATA (Root application data)
            // =============================================================================
            else if (queryString.includes('GetIndexPageData')) {
                console.log(
                    '🔍 MockLink: Handling GetIndexPageData query with indeedApplyableJobId:',
                    variables?.indeedApplyableJobId,
                );

                void simulateNetworkDelay(400, 800).then(() => {
                    // Mock comprehensive applyParameters response
                    const indeedApplyableJobId = variables?.indeedApplyableJobId || 'mock_job_123';
                    const mockDraftId = `draft_${indeedApplyableJobId.slice(0, 8)}`;

                    observer.next({
                        data: {
                            applyParameters: {
                                __typename: 'ApplyParametersPayload',
                                iaUid: 'mock_ia_uid_123', // Indicates authenticated user

                                // Job Details
                                jobDetails: {
                                    __typename: 'JobDetails',
                                    id: indeedApplyableJobId,
                                    title: 'ソフトウェアエンジニア',
                                    indeedJobKey: `job_key_${indeedApplyableJobId.slice(0, 8)}`,
                                    location: '東京都渋谷区',
                                    locale: 'ja_JP',
                                    employer: {
                                        __typename: 'Employer',
                                        name: '株式会社サンプル',
                                    },

                                    // Field Requirements
                                    standardFields: {
                                        __typename: 'StandardFields',
                                        phone: {
                                            __typename: 'PhoneField',
                                            requirement: 'REQUIRED',
                                        },
                                        coverLetter: {
                                            __typename: 'CoverLetterField',
                                            requirement: 'OPTIONAL',
                                        },
                                        resume: {
                                            __typename: 'ResumeField',
                                            requirement: 'REQUIRED',
                                        },
                                        name: {
                                            __typename: 'NameField',
                                            format: 'FIRST_LAST',
                                        },
                                        resumeFields: {
                                            __typename: 'ResumeFields',
                                            namePronunciation: {
                                                __typename: 'NamePronunciationField',
                                                requirement: 'OPTIONAL',
                                            },
                                            dateOfBirth: {
                                                __typename: 'DateOfBirthField',
                                                requirement: 'REQUIRED',
                                            },
                                            gender: {
                                                __typename: 'GenderField',
                                                requirement: 'OPTIONAL',
                                            },
                                            educations: {
                                                __typename: 'EducationsField',
                                                requirement: 'OPTIONAL',
                                            },
                                            location: {
                                                __typename: 'LocationField',
                                                requirement: 'REQUIRED',
                                            },
                                            workExperiences: {
                                                __typename: 'WorkExperiencesField',
                                                requirement: 'OPTIONAL',
                                            },
                                            languages: {
                                                __typename: 'LanguagesField',
                                                requirement: 'OPTIONAL',
                                            },
                                            certifications: {
                                                __typename: 'CertificationsField',
                                                requirement: 'OPTIONAL',
                                            },
                                            skills: {
                                                __typename: 'SkillsField',
                                                requirement: 'OPTIONAL',
                                            },
                                        },
                                    },

                                    // Question Configuration
                                    screenerQuestions: {
                                        __typename: 'ScreenerQuestions',
                                        sourceUrl: 'https://indeed.com/api/questions/123',
                                    },
                                    interviewScreenerQuestions: {
                                        __typename: 'InterviewScreenerQuestions',
                                        sourceUrl: 'https://indeed.com/api/interview-questions/123',
                                    },

                                    // Integration Details
                                    integration: {
                                        __typename: 'Integration',
                                        type: 'STANDARD',
                                        emailParameters: {
                                            __typename: 'EmailParameters',
                                            address: 'jobs@sample-company.com',
                                            locale: 'ja_JP',
                                        },
                                        postCallParameters: {
                                            __typename: 'PostCallParameters',
                                            uri: 'https://indeed.com/api/post-apply/123',
                                        },
                                        meta: {},
                                    },

                                    // Job Metadata
                                    source: 'INDEED',
                                    isSponsored: false,
                                    jobCountry: 'JP',
                                    finishApplicationUrl:
                                        'https://indeed.com/finish-application/123',
                                    from: 'SEARCH',
                                },

                                // Draft Status
                                draftDetails: {
                                    __typename: 'SmartApplyApplicationStateDraftDetails',
                                    id: mockDraftId,
                                    uri: `https://indeed.com/apply/draft/${mockDraftId}`,
                                    sponsored: false,
                                    locale: 'ja_JP',
                                    countryCode: 'JP',
                                    clientName: 'INTEGRATIONS_APPLY',
                                },

                                // Partner Configuration
                                partnerConfig: {
                                    __typename: 'PartnerConfig',
                                    name: 'Indeed',
                                    shortName: 'Indeed',
                                    editResumeUrl: 'https://indeed.com/edit-resume',
                                    supportsPartnerResume: true,
                                    partnerApiToken: 'mock_partner_token_123',
                                },
                            },
                        },
                    });
                    observer.complete();
                });
            }

            // =============================================================================
            // GET DRAFT ID (useNodeId Hook) - DEPRECATED, use GetIndexPageData instead
            // =============================================================================
            else if (queryString.includes('GetDraftId')) {
                console.log(
                    '🔍 MockLink: Handling GetDraftId query with indeedApplyableJobId:',
                    variables?.indeedApplyableJobId,
                );

                void simulateNetworkDelay(400, 800).then(() => {
                    // Mock response with a draft ID
                    const mockDraftId = `draft_${variables?.indeedApplyableJobId?.slice(0, 8) || 'mock'}`;

                    observer.next({
                        data: {
                            applyParameters: {
                                __typename: 'ApplyParametersPayload',
                                draftDetails: {
                                    __typename: 'SmartApplyApplicationStateDraftDetails',
                                    id: mockDraftId,
                                },
                            },
                        },
                    });
                    observer.complete();
                });
            }

            // =============================================================================
            // AUTO APPLY FLOW - DRAFT LIST
            // =============================================================================
            else if (queryString.includes('GetAutoApplyDraftList')) {
                void handleQuery(observer, 'GetAutoApplyDraftList', () => {
                    // Draft IDs for the auto-apply flow, with the number of screener questions
                    // still to answer. `draft-101` is the only one with an outstanding question,
                    // so it is the id to use when exercising the questions step locally.
                    const mockDrafts: [id: string, unfilledScreenerQuestionCount: number][] = [
                        ['draft-001', 0],
                        ['draft-002', 0],
                        ['draft-003', 0],
                        ['draft-004', 0],
                        ['draft-005', 0],
                        ['draft-101', 1],
                    ];

                    return {
                        findReviewBeforeSubmitDrafts: {
                            __typename: 'FindReviewBeforeSubmitDraftsPayload',
                            drafts: mockDrafts.map(([id, unfilledScreenerQuestionCount]) => ({
                                __typename: 'AutoApplyDraft',
                                applicationDraftId: id,
                                unfilledScreenerQuestionCount,
                            })),
                        },
                    };
                });
            }

            // =============================================================================
            // AUTO APPLY FLOW - SCREENER PREFILL STATE (review disclaimer gating)
            // =============================================================================
            else if (queryString.includes('GetAutoApplyScreenerPrefillState')) {
                void handleQuery(observer, 'GetAutoApplyScreenerPrefillState', () => {
                    const draftId: string = variables?.input?.draftId || '';
                    return {
                        smartApplyContext: {
                            __typename: 'SmartApplyContext',
                            draftId,
                            screenerQuestionContext: {
                                __typename: 'SmartApplyScreenerQuestionContext',
                                screenerQuestions: getScreenerQuestions(draftId),
                            },
                        },
                    };
                });
            }

            // =============================================================================
            // AUTO APPLY FLOW - SCREENER QUESTIONS (smartApplyContext)
            // =============================================================================
            else if (queryString.includes('GetAutoApplyScreenerQuestions')) {
                void handleQuery(observer, 'GetAutoApplyScreenerQuestions', () => {
                    const draftId: string = variables?.input?.draftId || '';
                    // `nodeId` is now the SmartApplyApplicationState IRI; the local store is keyed by
                    // the plain draft id (that is what SaveSQApplicationData persists under), so look
                    // up saved answers by draftId to keep the round-trip working.
                    const nodeId: string = variables?.nodeId || draftId;
                    // Overlay the answers persisted by SaveSQApplicationData so they reappear on reload.
                    const savedState = querySmartApplyState(draftId);

                    return {
                        smartApplyContext: {
                            __typename: 'SmartApplyContext',
                            draftId,
                            jobContext: {
                                __typename: 'SmartApplyJobContext',
                                employer: {
                                    __typename: 'JobContextEmployer',
                                    apiToken: 'mock_employer_api_token',
                                },
                            },
                            screenerQuestionContext: {
                                __typename: 'SmartApplyScreenerQuestionContext',
                                screenerQuestions: getScreenerQuestions(draftId),
                            },
                        },
                        smartApplyApplicationState: {
                            __typename: 'SmartApplyApplicationState',
                            id: nodeId,
                            questionsAndAnswers: {
                                __typename: 'ScreenerQuestionsAndAnswersSmartApplyApplicationState',
                                screener: savedState?.questionsAndAnswers?.screener ?? [],
                            },
                        },
                    };
                });
            }

            // =============================================================================
            // AUTO APPLY FLOW - JOB INFO HEADER
            // =============================================================================
            else if (queryString.includes('GetJobInfoHeaderData')) {
                void handleQuery(observer, 'GetJobInfoHeaderData', () => {
                    const applicationDraftId = variables?.input?.applicationDraftId || '';

                    // Map draftId (e.g. 'draft-005') to mock job data
                    // Parse the numeric part to look up in mock applications array
                    const draftIndex = applicationDraftId
                        ? parseInt(applicationDraftId.replace('draft-', ''), 10) - 1
                        : -1;

                    // Mock application data matching mockApplications in mockData.ts
                    const mockJobs = [
                        {
                            jobTitle: 'Customer Support Team Lead',
                            jobLocation: 'Austin, TX',
                            employerName: 'BrightConnect Solutions',
                        },
                        {
                            jobTitle: 'Customer Service Supervisor',
                            jobLocation: 'Austin, TX',
                            employerName: 'BrightConnect Solutions',
                        },
                        {
                            jobTitle: 'Call Center Manager',
                            jobLocation: 'Austin, TX',
                            employerName: 'BrightConnect Solutions',
                        },
                        {
                            jobTitle: 'Customer Service Supervisor',
                            jobLocation: 'Austin, TX',
                            employerName: 'BrightConnect Solutions',
                        },
                        {
                            jobTitle: 'Customer Experience Manager',
                            jobLocation: 'Austin, TX',
                            employerName: 'BrightConnect Solutions',
                        },
                    ];

                    const jobData =
                        draftIndex >= 0 && draftIndex < mockJobs.length
                            ? mockJobs[draftIndex]
                            : mockJobs[0];

                    return {
                        findReviewBeforeSubmitDraft: {
                            __typename: 'FindReviewBeforeSubmitDraftPayload',
                            draft: {
                                __typename: 'AutoApplyDraft',
                                applicationDraftId,
                                iaUid: `ia-${applicationDraftId}`,
                                jobTitle: jobData.jobTitle,
                                jobLocation: jobData.jobLocation,
                                employerName: jobData.employerName,
                                applyableJob: {
                                    __typename: 'ReviewBeforeSubmitApplyableJob',
                                    clientJobRef: `job-key-${draftIndex + 1}`,
                                },
                            },
                        },
                    };
                });
            }

            // =============================================================================
            // AUTO APPLY FLOW - CONTACT INFO
            // =============================================================================
            else if (queryString.includes('GetAutoApplyContactInfoFormData')) {
                void handleQuery(observer, 'GetAutoApplyContactInfoFormData', () => {
                    // nodeId IS the draftId (SmartApplyApplicationState uses draftId as its node ID)
                    const draftId = variables?.nodeId || '';
                    const draftIndex = draftId
                        ? parseInt(draftId.replace('draft-', ''), 10) - 1
                        : -1;

                    // Mock contact info per draft (varies slightly for realism)
                    const mockContacts = [
                        {
                            firstName: 'Ichiko',
                            lastName: 'Yuzawa',
                            email: 'ichiko.yuzawa@email.com',
                            phoneNumber: '',
                        },
                        {
                            firstName: 'Ichiko',
                            lastName: 'Yuzawa',
                            email: 'ichiko.yuzawa@email.com',
                            phoneNumber: '+1 (555) 123-4567',
                        },
                        {
                            firstName: 'Ichiko',
                            lastName: 'Yuzawa',
                            email: 'ichiko.yuzawa@email.com',
                            phoneNumber: '',
                        },
                    ];

                    const contactData =
                        draftIndex >= 0 && draftIndex < mockContacts.length
                            ? mockContacts[draftIndex]
                            : mockContacts[0];

                    return {
                        smartApplyApplicationState: {
                            __typename: 'SmartApplyApplicationState',
                            id: draftId,
                            draftId: draftId,
                            contactInfo: {
                                __typename: 'ContactInfoSmartApplyApplicationState',
                                firstName: contactData.firstName,
                                lastName: contactData.lastName,
                                email: contactData.email,
                                phoneNumber: contactData.phoneNumber,
                                firstNamePronunciation: null,
                                lastNamePronunciation: null,
                                dateOfBirth: null,
                                gender: null,
                                area: null,
                                city: 'Austin',
                                country: 'US',
                            },
                        },
                    };
                });
            }

            // =============================================================================
            // AUTO APPLY FLOW - CONTACT INFO MUTATION
            // =============================================================================
            else if (queryString.includes('UpdateAutoApplyContactInfo')) {
                void handleQuery(observer, 'UpdateAutoApplyContactInfo', () => {
                    const updateInput = variables?.updateContactInfoInput || {};

                    return {
                        smartApplyApplicationState: {
                            __typename: 'SmartApplyApplicationStateMutations',
                            updateContactInfo: {
                                __typename: 'UpdateContactInfoSmartApplyApplicationStatePayload',
                                state: {
                                    __typename: 'ContactInfoSmartApplyApplicationState',
                                    firstName: updateInput.firstName || null,
                                    lastName: updateInput.lastName || null,
                                    firstNamePronunciation:
                                        updateInput.firstNamePronunciation || null,
                                    lastNamePronunciation:
                                        updateInput.lastNamePronunciation || null,
                                    email: updateInput.email || null,
                                    phoneNumber: updateInput.phoneNumber || null,
                                    dateOfBirth: updateInput.dateOfBirth || null,
                                    gender: updateInput.gender || null,
                                    area: updateInput.area || null,
                                    city: updateInput.city || null,
                                    country: updateInput.country || null,
                                },
                                status: {
                                    __typename: 'UpdateContactInfoSmartApplyApplicationStatus',
                                    updateProfile: true,
                                },
                            },
                        },
                    };
                });
            }

            // =============================================================================
            // AUTO APPLY FLOW - SUCCESS PAGE (current user email)
            // =============================================================================
            else if (queryString.includes('SuccessPage')) {
                void handleQuery(observer, 'SuccessPage', () => {
                    return {
                        autoApplyInfo: {
                            __typename: 'AutoApplyInfoPayload',
                            autoApplyStatus: 'DISABLED',
                            mode: 'APPLY_FOR_ME',
                        },
                        currentUser: {
                            __typename: 'CurrentUserPayload',
                            account: {
                                __typename: 'PassportAccount',
                                accountProfile: {
                                    __typename: 'AccountProfile',
                                    email: {
                                        __typename: 'Email',
                                        emailAddress: 'ichiko.yuzawa@email.com',
                                    },
                                },
                            },
                        },
                    };
                });
            }

            // =============================================================================
            // AUTO APPLY FLOW - SUCCESS PAGE (draft states)
            // =============================================================================
            else if (queryString.includes('ApplicationStatus')) {
                void handleQuery(observer, 'ApplicationStatus', () => {
                    const applicationDraftId = variables?.input?.filter?.applicationDraftId || '';

                    const draftIndex = applicationDraftId
                        ? parseInt(applicationDraftId.replace('draft-', ''), 10) - 1
                        : -1;

                    const mockJobs = [
                        {
                            jobTitle: 'Customer Support Team Lead',
                            jobLocation: 'Austin, TX',
                            employerName: 'BrightConnect Solutions',
                        },
                        {
                            jobTitle: 'Customer Service Supervisor',
                            jobLocation: 'Austin, TX',
                            employerName: 'BrightConnect Solutions',
                        },
                        {
                            jobTitle: 'Call Center Manager',
                            jobLocation: 'Austin, TX',
                            employerName: 'BrightConnect Solutions',
                        },
                        {
                            jobTitle: 'Customer Service Supervisor',
                            jobLocation: 'Austin, TX',
                            employerName: 'BrightConnect Solutions',
                        },
                        {
                            jobTitle: 'Customer Experience Manager',
                            jobLocation: 'Austin, TX',
                            employerName: 'BrightConnect Solutions',
                        },
                    ];

                    const jobData =
                        draftIndex >= 0 && draftIndex < mockJobs.length
                            ? mockJobs[draftIndex]
                            : mockJobs[0];

                    // draft-003 returns DRAFT state to demo the error UI
                    const state = applicationDraftId === 'draft-003' ? 'DRAFT' : 'SUBMITTED';

                    return {
                        findApplicationDrafts: {
                            __typename: 'FindApplicationDraftsConnection',
                            edges: [
                                {
                                    __typename: 'DraftManagementApplicationDraftEdge',
                                    node: {
                                        __typename: 'DraftManagementApplicationDraft',
                                        applicationDraftId,
                                        draftMetaData: {
                                            __typename: 'DraftManagementDraftMetaData',
                                            state,
                                        },
                                        applyableJob: {
                                            __typename: 'DraftManagementApplyableJob',
                                            jobId: `job-${draftIndex + 1}`,
                                            jobTitle: jobData.jobTitle,
                                            jobLocation: jobData.jobLocation,
                                            clientJobRef: null,
                                            employer: {
                                                __typename: 'DraftManagementEmployer',
                                                name: jobData.employerName,
                                            },
                                        },
                                    },
                                },
                            ],
                        },
                    };
                });
            }

            // =============================================================================
            // AUTO APPLY SETUP - LEGAL DISCLAIMER PAGE OPERATIONS
            // =============================================================================
            else if (queryString.includes('GetResumeType')) {
                console.log('🔍 MockLink: Handling GetResumeType query');
                void handleQuery(observer, 'GetResumeType', () => {
                    return {
                        // Dual-resume migration (PROF-9839). See
                        // `MOCK_DUAL_RESUMES_ENABLED` at the top of this file.
                        jobSeekerProfileDualResumesEnabled: buildMockDualResumesEnabledPayload(),
                        jobSeekerResumes: buildMockJobSeekerResumesPayload(),
                        autoApplyInfo: {
                            __typename: 'AutoApplyInfoPayload',
                            selectedResumeType: 'INDEED_RESUME',
                        },
                        jobSeekerProfile: {
                            __typename: 'JobSeekerProfilePayload',
                            profile: {
                                __typename: 'JobSeekerProfile',
                                resume: [
                                    {
                                        __typename: 'JobSeekerProfileResume',
                                        id: 'indeed_resume_1',
                                        resumeType: 'INDEED_RESUME',
                                    },
                                ],
                                fileInformation: [
                                    {
                                        __typename: 'JobSeekerProfileFile',
                                        id: MOCK_FILE_RESUME_ID,
                                        fileType: 'RESUME',
                                    },
                                ],
                            },
                        },
                    };
                });
            } else if (queryString.includes('TestRbsCurrentUser')) {
                void handleQuery(observer, 'TestRbsCurrentUser', () => {
                    return {
                        currentUser: {
                            __typename: 'CurrentUserPayload',
                            account: {
                                __typename: 'PassportAccount',
                                id: 'passport-account-1',
                                accountId: 12345,
                                accountProfile: {
                                    __typename: 'AccountProfile',
                                    email: {
                                        __typename: 'Email',
                                        emailAddress: 'janett.smith@example.com',
                                    },
                                },
                            },
                        },
                    };
                });
            }

            // =============================================================================
            // FALLBACK & ERROR HANDLING
            // =============================================================================
            else {
                console.warn(
                    '⚠️ MockLink: Unhandled operation:',
                    operation.operationName || 'Unknown',
                );
                console.warn('Query:', queryString.slice(0, 100) + '...');

                // Return empty successful response to prevent errors
                setTimeout(() => {
                    observer.next({ data: {} });
                    observer.complete();
                }, 100);
            }
        });
    });
};

export default createMockLink;
